import moment from 'moment';
import { type ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks/useLocalize';
import * as graphql from '@treams/treams-network';
import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Button } from '@treams/treams-ui/common/button/Button';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { Popover } from '@treams/treams-ui/common/Popover';
import { Paragraph } from '@treams/treams-ui/common/Text';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';
import { PencilIcon } from '@treams/treams-ui/icons';
import { tokens } from '@treams/treams-ui/theme/tokens';

const RevisionCard = styled(FlexGap)`
	background: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.control}px;

	.ql-editor p {
		font-size: ${tokens.typography.sizes.body}px;
		color: ${tokens.text.primary};
		margin: 0;
	}

	.ql-editor p + p {
		margin-top: ${tokens.gap.inline}px;
	}
`;

export type PostShareAnswerRevision = {
	actor?: { firstName?: string | null; lastName?: string | null } | null;
	changedAt?: string | null;
	openQuestion?: ComponentProps<typeof QuillText>['value'] | null;
	competency?: number | null;
};

export default function PostShareAnswerEdits(props: {
	revisions: PostShareAnswerRevision[];
	sectionItem: graphql.EvaluationSectionItem;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const [open, setOpen] = useState(false);

	if (props.revisions.length === 0) {
		return null;
	}

	const sortedRevisions = [...props.revisions].reverse().sort((left, right) => {
		if (!left.changedAt || !right.changedAt) {
			return 0;
		}
		return moment(right.changedAt).valueOf() - moment(left.changedAt).valueOf();
	});

	return (
		<FlexGap justifyContent="flex-end" role="group" aria-label="conversation-answer-edits">
			<Popover.Target
				visible={open}
				horizontalAlign="right"
				onDismiss={() => setOpen(false)}
				contents={
					<Box p={3} minWidth={260} maxWidth={400} style={{ maxHeight: 380, overflowY: 'auto' }}>
						<FlexGap flexDirection="column" gap={tokens.gap.list}>
							{sortedRevisions.map((revision, index) => {
								const name = actorName(revision.actor);

								return (
									<RevisionCard
										key={index}
										flexDirection="column"
										gap={tokens.gap.element}
										role="group"
										aria-label="conversation-answer-edit"
									>
										{(name || revision.changedAt) && (
											<FlexGap
												alignItems="center"
												justifyContent="space-between"
												gap={tokens.gap.element}
											>
												{name && (
													<FlexGap alignItems="center" gap={tokens.gap.element} minWidth={0}>
														<Avatar
															size={20}
															user={{
																firstName: revision.actor?.firstName ?? '',
																lastName: revision.actor?.lastName ?? '',
															}}
														/>
														<Paragraph
															small
															fontWeight="medium"
															style={{ color: tokens.text.primary }}
														>
															{name}
														</Paragraph>
													</FlexGap>
												)}
												{revision.changedAt && (
													<Paragraph
														small
														title={moment(revision.changedAt).format('LLL')}
														style={{
															color: tokens.text.tertiary,
															whiteSpace: 'nowrap',
															flexShrink: 0,
														}}
													>
														{moment(revision.changedAt).fromNow()}
													</Paragraph>
												)}
											</FlexGap>
										)}
										{revision.openQuestion && <QuillText value={revision.openQuestion} />}
										{revision.competency !== null &&
											revision.competency !== undefined &&
											competencyTitle(props.sectionItem, revision.competency, localize)}
									</RevisionCard>
								);
							})}
						</FlexGap>
					</Box>
				}
			>
				<Button
					type="textIcon"
					small
					icon={
						<span aria-hidden>
							<PencilIcon />
						</span>
					}
					title={t('evaluations.submission.answer.edited')}
					onClick={() => setOpen((current) => !current)}
				/>
			</Popover.Target>
		</FlexGap>
	);
}

function actorName(
	actor: { firstName?: string | null; lastName?: string | null } | null | undefined,
) {
	return [actor?.firstName, actor?.lastName].filter(Boolean).join(' ');
}

function competencyTitle(
	sectionItem: graphql.EvaluationSectionItem,
	index: number,
	localize: ReturnType<typeof useLocalize>['localize'],
) {
	const level = sectionItem.competency?.competency?.levels[index];
	if (!level) {
		return null;
	}

	return (
		<Paragraph style={{ color: tokens.text.primary }}>{localize(level.level.title).text}</Paragraph>
	);
}
